import { type UseFormReturn } from 'react-hook-form-7'
import { type RetailerLocationStoreGroup } from 'domains/account-management/components/retailer-location-store-group-select/utils/types'
import { type CreateEnterpriseAccountMutationVariables } from 'domains/account-management/api'
import { type OptionType as RoleSelectOptionType } from 'domains/account-management/components/role-select/utils/AccountManagementRoleSelect.types'

interface OptionType {
  label: string
  value: string
}

export interface FormData
  extends Omit<
    CreateEnterpriseAccountMutationVariables,
    'roleIds' | 'partnerIds' | 'retailerLocationIds' | 'retailerIds'
  > {
  canonicalId?: string
  roles: RoleSelectOptionType[]
  partners: OptionType[]
  retailers: OptionType[]
  retailerLocations: OptionType[]
  retailerLocationsStoreGroups: RetailerLocationStoreGroup
  hasAllPartnersFullAccess: boolean
  hasFullPartnerAccess: boolean
  hasFullRetailerAccess: boolean
  createAnother: boolean
}

/**
 * An enum for defining all the redundant types of terminologies we have for
 * speaking about retailers.
 */
export enum RetailerTerminology {
  /**
   * The most common terminology found across our code base.
   * "Partners" are the top level organization which is comprised of subsidiary companies.
   * Eg: "Loblaws" is the partner and "No Frills" is the retailer.
   * or "Albertsons" is the partner and "Safeway" is the retailer.
   */
  InternalTerms,
  /**
   * Retailers refer to "partners" as "retailers", and "retailers" as "banners". Confusing? Yes.
   */
  RetailerTerms,
}

export interface AccountFormProps {
  /** Whether to show full access toggle */
  showAllPartnersFullAccessToggle?: boolean
  /** Changes the terminology displayed for referring to retailers */
  retailerTerminology: RetailerTerminology
  /** Whether to allow giving full access to all partners */
  allowAllPartnersFullAccess: boolean
  /** Whether to allow giving full partner access */
  allowFullPartnerAccess: boolean
  /** Whether to allow giving full retailer access */
  allowFullRetailerAccess: boolean
  /** If the form is updating an account */
  isUpdate: boolean
  /** Controls showing the create another checkbox */
  allowCreateAnother?: boolean
  /** Form viewed in the context of given partner id (blank for admin view) */
  partnerId?: string
  /** Initial state, used when editing users */
  initialState?: Partial<FormData>
  /** What will be triggered when submitting the form */
  onFormSubmit: (formData: FormData, form: UseFormReturn<FormData>) => Promise<void>
  /** Indicator to know when the form is under submission */
  formLoading: boolean
  /** A component to show as a notification when the submission suceeded */
  SuccessNotification: React.ComponentType
  /** A component to show as a notification when the submission failed */
  ErrorNotification: React.ComponentType
  /** Fields in this object will be disabled */
  disableFields?: Partial<Record<keyof FormData, boolean>>
  /** Is the current account read-only? */
  isCurrentAccountReadOnly?: boolean
}
